import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useConductorAuth } from '../contexts/ConductorAuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, AlertCircle, ArrowLeft, IdCard, Car, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export const Login = () => {
  const [isConductor, setIsConductor] = useState(false);
  const [identifier, setIdentifier] = useState(''); // email o RUT
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const { login: loginConductor } = useConductorAuth();
  const navigate = useNavigate();

  // DoSoft brand colors
  const DOSOFT_TEAL = '#6BA3B7';
  const DOSOFT_GRAY = '#4A4A4A';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isConductor) {
        const data = await loginConductor(identifier.trim(), password);
        toast.success(`¡Bienvenido, ${data.nombre}!`);
        if (data.must_change_password) {
          navigate('/conductor/cambiar-password', { replace: true });
        } else {
          navigate('/conductor', { replace: true });
        }
      } else {
        const user = await login(identifier, password);
        toast.success('¡Bienvenido!');
        if (user.role === 'superadmin') {
          navigate('/superadmin/empresas');
        } else if (user.role === 'admin') {
          navigate('/admin/calendario');
        } else {
          navigate('/cliente/calendario');
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
      toast.error('Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (toConductor) => {
    if (loading) return;
    setIsConductor(toConductor);
    setIdentifier('');
    setPassword('');
    setError('');
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${DOSOFT_TEAL} 0%, ${DOSOFT_GRAY} 50%, #2d3748 100%)`,
        backgroundSize: '400% 400%',
        animation: 'gradient 15s ease infinite'
      }}>
      <style>{`
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>

      <div className="absolute inset-0 bg-black/10"></div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm">
          <div className="flex justify-center mb-6">
            <img
              src="https://customer-assets.emergentagent.com/job_30a67381-0193-4972-adc8-4683263d2f57/artifacts/0sxu9ue3_Logo%20DoSoft.png"
              alt="DoSoft"
              className="h-14 w-auto"
            />
          </div>

          <h2 className="text-2xl font-bold text-center mb-2 font-['Manrope']" style={{ color: DOSOFT_GRAY }}>
            Sistema de Gestión de Traslados
          </h2>
          <p className="text-center text-gray-600 mb-6 text-sm">
            {isConductor ? 'Acceso para conductores' : 'Ingresa tus credenciales para continuar'}
          </p>

          {/* Toggle de modo */}
          <div className="bg-gray-100 rounded-lg p-1 flex gap-1 mb-6" data-testid="login-mode-toggle">
            <button
              type="button"
              onClick={() => switchMode(false)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition ${
                !isConductor ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="mode-admin"
            >
              <ShieldCheck size={16} />
              Admin / Cliente
            </button>
            <button
              type="button"
              onClick={() => switchMode(true)}
              className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition ${
                isConductor ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'
              }`}
              data-testid="mode-conductor"
            >
              <Car size={16} />
              Conductor
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2 text-red-700" data-testid="login-error">
              <AlertCircle size={18} />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="identifier" className="text-sm font-medium">
                {isConductor ? 'RUT' : 'Correo Electrónico'}
              </Label>
              <div className="relative mt-1.5">
                {isConductor ? (
                  <IdCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                ) : (
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                )}
                <Input
                  id="identifier"
                  type={isConductor ? 'text' : 'email'}
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  className="pl-10"
                  placeholder={isConductor ? '12.345.678-9' : 'correo@empresa.com'}
                  autoComplete={isConductor ? 'username' : 'email'}
                  required
                  data-testid={isConductor ? 'rut-input' : 'email-input'}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              disabled={loading}
              data-testid="login-button"
              style={{ backgroundColor: DOSOFT_TEAL }}
            >
              {loading ? 'Ingresando...' : 'Iniciar Sesión'}
            </Button>

            {!isConductor && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/recuperar-password')}
                  className="text-sm hover:underline"
                  style={{ color: DOSOFT_TEAL }}
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>
            )}

            <Button
              type="button"
              variant="ghost"
              className="w-full h-10 text-gray-600 hover:text-gray-800"
              onClick={() => navigate('/')}
              data-testid="back-to-landing-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver al Inicio
            </Button>
          </form>
        </div>

        <p className="text-center mt-6 text-white text-sm drop-shadow-lg">
          © {new Date().getFullYear()} DoSoft
        </p>
      </div>
    </div>
  );
};
